import { typedSupabase } from "../../lib/typed-supabase";
import type { IService } from "../service-registry";
import type {
  Notification,
  NotificationsResponse,
} from "../../types/notification";
import { BaseNotificationService, type NotificationServiceResponse } from "./base-notification.service";

/**
 * Notification Reader Interface
 * Handles read operations: Get, Filter, Count
 */
export interface INotificationReader extends IService {
  getNotifications(
    userId: string,
    options?: { limit?: number; includeRead?: boolean; offset?: number }
  ): Promise<NotificationServiceResponse<NotificationsResponse>>;

  getUnreadCount(userId: string): Promise<NotificationServiceResponse<number>>;

  getNotificationById(
    notificationId: string
  ): Promise<NotificationServiceResponse<Notification | null>>;
}

/**
 * Notification Reader Service
 * Handles all Read operations including filtering, pagination, and aggregation
 * Single responsibility: Query layer
 *
 * Optimization: Fetches count in same query as notifications (no N+1)
 *
 * @example
 * ```typescript
 * const result = await notificationReader.getNotifications("user-123", {
 *   limit: 50,
 *   includeRead: false,
 * });
 *
 * if (result.data) {
 *   console.log(`Got ${result.data.notifications.length} unread notifications`);
 * }
 * ```
 */
class NotificationReader
  extends BaseNotificationService
  implements INotificationReader
{
  readonly name = "notification-reader" as const;
  protected readonly serviceName = this.name;

  /**
   * Get notifications for a user with filtering and pagination
   *
   * Optimizations:
   * - Fetches exact count in same query (no N+1)
   * - Respects limit parameter for pagination
   * - Supports filtering by read/unread status
   * - Sorted by creation date (newest first)
   *
   * @param userId - User ID to fetch notifications for
   * @param options - Filtering and pagination options
   * @param options.limit - Maximum notifications to return (default: 50)
   * @param options.includeRead - Include read notifications (default: false)
   * @returns Notifications array with metadata
   *
   * @example
   * ```typescript
   * // Get only unread notifications
   * const unread = await notificationReader.getNotifications("user-123", {
   *   limit: 20,
   *   includeRead: false,
   * });
   *
   * // Get all notifications (read and unread)
   * const all = await notificationReader.getNotifications("user-123", {
   *   includeRead: true,
   * });
   * ```
   */
  async getNotifications(
    userId: string,
    options: { limit?: number; includeRead?: boolean; offset?: number } = {}
  ): Promise<NotificationServiceResponse<NotificationsResponse>> {
    if (!userId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "userId is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<NotificationsResponse>;
    }

    const { limit = 50, includeRead = false, offset = 0 } = options;

    if (limit < 1 || limit > 1000) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "limit must be between 1 and 1000",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<NotificationsResponse>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation<NotificationsResponse>(
      "NOTIFICATION_GET",
      async () => {
        // Build query with count
        let query = typedSupabase
          .from("notifications")
          .select("*", { count: "exact" })
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .range(offset, offset + limit - 1);

        // Filter by read status if needed
        if (!includeRead) {
          query = query.eq("is_read", false);
        }

        // Only fetch notifications from the last 365 days to prevent loading too much history
        // This is especially important for read notifications that haven't been cleaned up
        const oneYearAgo = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
        query = query.gte("created_at", oneYearAgo.toISOString());

        const { data: notifications, error, count } = await query;

        return {
          data: {
            notifications: notifications || [],
            count: count || 0,
            hasMore: (notifications?.length || 0) >= limit,
          } as NotificationsResponse,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics("NOTIFICATION_GET", performance.now() - startTime, {
        userId,
        limit,
        includeRead,
        notificationCount: response.data?.notifications?.length || 0,
        totalCount: response.data?.count || 0,
      });
    }

    return response;
  }

  /**
   * Get unread notification count for a user
   *
   * Efficient query using head=true to minimize data transfer.
   *
   * @param userId - User ID to count unread notifications for
   * @returns Count of unread notifications
   *
   * @example
   * ```typescript
   * const result = await notificationReader.getUnreadCount("user-123");
   * if (result.data) {
   *   console.log(`${result.data} unread notifications`);
   * }
   * ```
   */
  async getUnreadCount(userId: string): Promise<NotificationServiceResponse<number>> {
    if (!userId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "userId is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation<number>(
      "NOTIFICATION_UNREAD_COUNT",
      async () => {
        const { count, error } = await typedSupabase
          .from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("is_read", false);

        return {
          data: count || 0,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics("NOTIFICATION_UNREAD_COUNT", performance.now() - startTime, {
        userId,
        unreadCount: response.data || 0,
      });
    }

    return response;
  }

  /**
   * Get a single notification by ID
   *
   * Returns null if notification not found (not an error).
   *
   * @param notificationId - ID of notification to retrieve
   * @returns Notification or null if not found
   *
   * @example
   * ```typescript
   * const result = await notificationReader.getNotificationById("notif-123");
   * if (result.data) {
   *   console.log("Found:", result.data.message_preview);
   * } else {
   *   console.log("Not found");
   * }
   * ```
   */
  async getNotificationById(
    notificationId: string
  ): Promise<NotificationServiceResponse<Notification | null>> {
    if (!notificationId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "notificationId is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<Notification | null>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation<Notification | null>(
      "NOTIFICATION_GET_BY_ID",
      async () => {
        const { data, error } = await typedSupabase
          .from("notifications")
          .select("*")
          .eq("id", notificationId)
          .maybeSingle();

        return {
          data: data || null,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics("NOTIFICATION_GET_BY_ID", performance.now() - startTime, {
        notificationId,
        found: response.data !== null,
      });
    }

    return response;
  }
}

export const notificationReader = new NotificationReader();
export { NotificationReader };
